// Importing necessary React libraries and components
import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogTitle,
  Tab,
  Tabs,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { styled } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import BookingTypes from "./bookingType";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import BookingIframeGenerator from "../../../../../platform/util/iframe/bookingIframeGenerator";
import { usePermissions } from "../../../../../platform/hooks/usePermissions";
import { GET_BOOKING_TYPES } from "../../../../../platform/graphql/queries";
import { GET_BILLING_SUBSCRIPTION, GET_PLANS } from "../../../../../platform/graphql/queries";
import UpgradePlan from "../../../../../platform/util/upgrade/upgradePlan";
import {
  FeatureAccess,
  getFeatureAccess,
} from "../../../../../platform/util/featureAccess";
import {
  hasPaidBookingFeatures,
  hasStandaloneBookingAccess,
  isProductVisible,
} from "../../../../../core/business/productAccess";

const LOCAL_WORKSPACE_PRODUCTS_KEY = "planyLocalWorkspaceProducts";
const ACTIVE_BOOKING_STATUSES = new Set(["trialing", "active", "past_due", "canceling"]);
const FREE_BOOKING_TYPE_LIMIT = 2;

function getActiveProductId(location) {
  const params = new URLSearchParams(location?.search || "");
  const fromQuery = String(params.get("v") || "").toLowerCase();
  if (fromQuery === "booking" || fromQuery === "care") return fromQuery;
  return String(location?.pathname || "").includes("/booking") ? "booking" : "care";
}

function readLocalWorkspaceProducts(businessId) {
  if (!businessId || typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_WORKSPACE_PRODUCTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed[String(businessId)] || {}) : {};
  } catch {
    return {};
  }
}

const TOUR_ONLY_BOOKING_TYPE_KEYWORDS = ["tour", "visit", "walkthrough"];

function isTourOnlyConciergeBookingType(bookingType) {
  if (!bookingType?.createdByAgentId) return false;

  const title = String(bookingType?.title || "").toLowerCase();
  const link = String(bookingType?.link || "").toLowerCase();

  return TOUR_ONLY_BOOKING_TYPE_KEYWORDS.some(
    (keyword) => title.includes(keyword) || link.includes(keyword)
  );
}

// Custom styled container
const ContentHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(3, 3, 2),
  boxSizing: "border-box",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2, 1.5),
  },
}));

const ContentHead = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  justifyContent: "space-between",
}));

const Top = styled(Box)(({ theme }) => ({
  display: "flex",
  minHeight: 44,
  alignItems: "center",
  justifyContent: "space-between",
}));

// Main Home component
const BookingTypeHome = (props) => {
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;
  const bookingCreateAccess = getFeatureAccess(
    providerUser,
    FeatureAccess.BOOKING_TYPE_CREATE
  );
  const standardPlanRequired = bookingCreateAccess.requiredPlanId;

  const selectedBusiness = useSelectedBusiness();
  const location = useLocation();
  const activeProductId = useMemo(() => getActiveProductId(location), [location]);
  const businessId = Number(selectedBusiness?.businessBasic?.id || providerUser?.defaultBusinessId || 0) || null;
  const isCareBusiness = Boolean(
    selectedBusiness?.careSettings?.afhLicenseNumber ||
    selectedBusiness?.businessBasic?.careSettings?.afhLicenseNumber
  );
  const [localWorkspaceProducts, setLocalWorkspaceProducts] = useState(() =>
    readLocalWorkspaceProducts(businessId)
  );
  useEffect(() => {
    setLocalWorkspaceProducts(readLocalWorkspaceProducts(businessId));
  }, [businessId, location.search]);
  const { data: bookingBillingData } = useQuery(GET_BILLING_SUBSCRIPTION, {
    variables: { businessId },
    skip: !businessId,
    fetchPolicy: "network-only",
  });
  const { data: plansData } = useQuery(GET_PLANS, {
    skip: !businessId,
  });
  const bookingBillingStatus = String(
    bookingBillingData?.getBillingSubscription?.status || ""
  ).toLowerCase();
  const hasBookingBillingAccess = ACTIVE_BOOKING_STATUSES.has(bookingBillingStatus);
  const isBookingContext = activeProductId === "booking";
  const hasStandaloneBooking =
    isBookingContext ||
    hasStandaloneBookingAccess(providerUser, isCareBusiness) ||
    isProductVisible(providerUser, "booking") ||
    hasBookingBillingAccess ||
    !!localWorkspaceProducts.booking;
  const hasAdvancedBookingAccess =
    hasPaidBookingFeatures(
      providerUser,
      bookingBillingData?.getBillingSubscription,
      plansData?.plans || [],
      isCareBusiness
    ) || hasBookingBillingAccess;
  const businessUrl = selectedBusiness?.businessBasic?.url || "";
  const effectiveStaffId = useMemo(() => {
    const rawStaffId = Number(providerUser?.staffId);
    if (Number.isFinite(rawStaffId) && rawStaffId > 0) return rawStaffId;

    const providerUserId = Number(providerUser?.userId);
    if (!Number.isFinite(providerUserId) || providerUserId <= 0) return null;

    const matchedStaff = (selectedBusiness?.businessStaffs || []).find(
      (staff) => Number(staff?.user?.id) === providerUserId
    );
    const matchedStaffId = Number(matchedStaff?.id);
    return Number.isFinite(matchedStaffId) && matchedStaffId > 0 ? matchedStaffId : null;
  }, [providerUser?.staffId, providerUser?.userId, selectedBusiness?.businessStaffs]);

  const {
    data: bookingTypesFallbackData,
    refetch: refetchBookingTypesFallback,
  } = useQuery(GET_BOOKING_TYPES, {
    variables: { staffId: Number(effectiveStaffId) },
    skip: !effectiveStaffId,
    fetchPolicy: "cache-and-network",
  });

  const resolvedBookingTypes =
    Array.isArray(props?.data) && props.data.length > 0
      ? props.data
      : bookingTypesFallbackData?.getBookingTypes || [];
  const visibleBookingTypes = useMemo(
    () => resolvedBookingTypes.filter((bookingType) => !isTourOnlyConciergeBookingType(bookingType)),
    [resolvedBookingTypes]
  );

  const resolvedRefetch = props?.refetch || refetchBookingTypesFallback;
  const [isDiabled, setIsDisabled] = useState(false);
  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
  const [upgradeFeatureName, setUpgradeFeatureName] = useState("Booking types");
  const currentLoginStaff = selectedBusiness?.businessStaffs?.find(
    (staff) => Number(staff.id) === Number(effectiveStaffId)
  );
  const hasReachedFreeBookingTypeLimit =
    !hasAdvancedBookingAccess &&
    visibleBookingTypes.length >= FREE_BOOKING_TYPE_LIMIT;

  const { canEditBookingType } = usePermissions();

  useEffect(() => {
    if (!effectiveStaffId || !currentLoginStaff?.id) {
      // Avoid transient first-load lock while auth/business context is still resolving.
      setIsDisabled(false);
      return;
    }
    const canEdit = canEditBookingType(Number(currentLoginStaff?.id));
    canEdit ? setIsDisabled(false) : setIsDisabled(true);
  }, [canEditBookingType, currentLoginStaff?.id, effectiveStaffId]);

  // Tab value state
  const [value, setValue] = React.useState(0);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParam = searchParams.get("tab");
    if (queryParam) {
      const index = parseInt(queryParam);
      if (!isNaN(index) && index - 1 >= 0 && index - 1 < 2) {
        setValue(index - 1);
      }
    }
  }, []);

  const handleChange = (bookingType, newValue) => {
    setValue(newValue);
    const searchParams = new URLSearchParams(window.location.search);
    if (newValue === 0) {
      searchParams.delete("scheduletab");
    } else if (newValue === 1) {
      searchParams.set("scheduletab", 1);
    }
    searchParams.set("tab", newValue + 1);
    const newUrl = window.location.pathname + "?" + searchParams.toString();
    window.history.pushState(null, null, newUrl);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEmbedClick = () => {
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleLinkClick = (bookingtypecategory) => {
    const formattedPage = bookingtypecategory.toLowerCase();
    const href = `/biz/create/${formattedPage}?back=1`;
    navigate(href);
  };

  const handleUpgradeBookingTypeLimit = () => {
    setUpgradeFeatureName("More than 2 booking types");
    setCheckPlanNeeded(true);
  };

  const handleAddBookingType = () => {
    if (!hasStandaloneBooking) {
      setUpgradeFeatureName("Booking types");
      setCheckPlanNeeded(true);
      return;
    }
    if (hasReachedFreeBookingTypeLimit) {
      handleUpgradeBookingTypeLimit();
      return;
    }
    handleLinkClick("event");
  };

  return (
    <>
      {checkPlanNeeded && (
        <UpgradePlan
          featureName={upgradeFeatureName}
          requiredPlanId={standardPlanRequired}
          applyExemption={false}
          pricingProduct="booking"
          onClose={() => setCheckPlanNeeded(false)}
        />
      )}
      {!hasStandaloneBooking && (
        <Box sx={{ px: 3, mb: 1 }}>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Booking type creation and advanced Booking features require a Booking subscription.
          </Alert>
          <Button size="small" variant="outlined" onClick={() => setCheckPlanNeeded(true)}>
            Subscribe to Booking
          </Button>
        </Box>
      )}
      <>
      <ContentHeader>
        <ContentHead>
          <Top>
            <Box sx={{ display: "flex", width: "100%" }}>
              <Box sx={{ flexGrow: 1 }} /> {/* This box will take up remaining space */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                <Button
                  disabled={isDiabled}
                  onClick={handleAddBookingType}
                  variant="outlined"
                  startIcon={<AddIcon />}
                >
                  Add Booking Types
                </Button>
                <IconButton onClick={handleMenuClick}>
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}                 
                >
                  <MenuItem  disabled={isDiabled}  onClick={handleEmbedClick}>Embed to your page</MenuItem>
                </Menu>
                <Dialog
                  open={dialogOpen}
                  onClose={handleDialogClose}
                  fullWidth
                  maxWidth="sm"
                >
                  <Box sx={{ p: 2 }}>
                    <BookingIframeGenerator businessUrl={businessUrl} />
                  </Box>
                  <DialogActions>
                    <Button onClick={handleDialogClose} variant="outlined">
                      Close
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            </Box>
          </Top>
        </ContentHead>
      </ContentHeader>

      <BookingTypes
        refetch={resolvedRefetch}
        user={providerUser.url}
        data={visibleBookingTypes}
        canCreateMoreBookingTypes={!hasReachedFreeBookingTypeLimit}
        onUpgradeBookingTypeLimit={handleUpgradeBookingTypeLimit}
      />
      </>
    </>
  );
};

export default BookingTypeHome;
